import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import stylesLayout from '@pages/catalog/ui/styles.module.scss'

import { CarouselCategoriesPreview , useCategorySlug } from '@entities/Categories'
import {
  ButtonProductFilters,
  ProductFilters,
  Products,
  useProductSort,
} from '@entities/Product'
import { SORT_PRODUCTS } from '@entities/Product/sort'

import { CartPreview } from '@features/cart'
import { useAddressStore } from '@features/shipping-methods'

import { CategoriesBranch } from '@widgets/categories-branch'




import SEO from '@shared/components/SEO'
import { URL_PAGE_CATALOG, URL_PAGE_CATALOG_1ST_LVL } from '@shared/contants'
import { BRAND_NAME } from '@shared/contants/app'
import {
  CategoriesBranchQuery,
  CategoriesPreviewQuery,
  useCategoriesBranchQuery,
  useCategoryQuery,
} from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Breadcrumbs from '@shared/ui/breadcrumbs'
import Container from '@shared/ui/container'
import PageGrid from '@shared/ui/page-grid'
import SelectSort from '@shared/ui/select-sort'
import TitleWithCount from '@shared/ui/title-with-count'
import Wrapper from '@shared/ui/wrapper'
import getNoun from '@shared/uttils/getNoun'

import styles from './styles.module.scss'

export default function CategoryChildrenCatalogPage () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('product')
  const { t: ts } = useTranslation('seo')

  const isTablet = useMediaQuery('(max-width: 991px)')
  
  const { sortKey, setSortKey } = useProductSort()
  
  const [ totalProducts, setTotalProducts ] = useState<number | null>(null)
  
  const { shopId } = useAddressStore()
  const categorySlug = useCategorySlug()

  const { data: category, loading: loadingCategory } = useCategoryQuery({
    variables: {
      shopId: shopId,
      slug: categorySlug,
    },
  })

  const categoryParentId = category?.category?.parent?.id
  const categoryParentSlug = category?.category?.parent?.slug

  const { data: categoriesBranch } = useCategoriesBranchQuery({
    variables: {
      shopId: shopId,
      parentId: categoryParentId,
    },
  })

  function transformToPreviewQuery (data?: CategoriesBranchQuery) {
    const category = data?.categoriesBranch?.find(
      (item) => item?.slug === categorySlug,
    )
    return {
      categories: (category?.childrens ?? []).map((item) => ({
        id: item?.id ?? '',
        name: item?.name ?? '',
        slug: item?.slug ?? '',
      })),
    }
  }

  const categoriesPreview = transformToPreviewQuery(categoriesBranch)

  return (
    <Container>
      <SEO
        title={category?.category?.name ? `${category.category.name} | ${BRAND_NAME}` : ts('catalog.title', { brand: BRAND_NAME })}
        description={category?.category?.name ? ts('catalog.categoryDescription', { category: category.category.name, brand: BRAND_NAME }) : ts('catalog.description', { brand: BRAND_NAME })}
      />
      <PageGrid aside={<CartPreview />}>
        <Wrapper className={styles.wrapper}>
          <div className={stylesLayout.grid}>
            <Breadcrumbs
              className={stylesLayout.breadcrumbs}
              links={[
                {
                  title: tn('catalog'),
                  to: URL_PAGE_CATALOG,
                },
                ...(category?.category?.parent
                  ? [
                    {
                      title: category.category.parent.name as string,
                      to: generatePath(URL_PAGE_CATALOG_1ST_LVL, { categorySlug: category.category.parent.slug as string }),
                    },
                  ]
                  : []),
                {
                  title: category?.category?.name as string,
                },
              ]}
              style={{
                marginBottom: '24px',
              }}
            />

            {!isTablet && (
              <div className={cn(stylesLayout.aside, styles.aside)}>
                <div className={styles.aside}>
                  <CategoriesBranch className={styles.aside__content} />
                </div>

                { category?.category?.id && <ProductFilters categoryId={category?.category?.id} /> }
              </div>
            )}

            <div className={cn(stylesLayout.content)}>
              <div className={styles.root__top}>
                {category?.category?.name && (
                  <TitleWithCount
                    className={styles.root__title}
                    title={category?.category?.name as string}
                  >
                    {totalProducts !== null && (
                      <>
                        {totalProducts}{' '}
                        {getNoun(
                          totalProducts as number,
                          tp('product.1'),
                          tp('product.2'),
                          tp('product.3'),
                        )}
                      </>
                    )}
                  </TitleWithCount>
                )}

                {isTablet && (
                  <ButtonProductFilters
                    className={styles['root__filter-button']}
                  />
                )}

                {(totalProducts === null || totalProducts > 0) && (
                  <SelectSort
                    className={styles.root__sort}
                    fields={SORT_PRODUCTS}
                    value={sortKey}
                    onChange={(key) => {
                      setSortKey(key)
                    }}
                  />
                )}
              </div>

              {categoriesPreview.categories && (
                <CarouselCategoriesPreview
                  className={styles['categories-top']}
                  data={categoriesPreview as CategoriesPreviewQuery}
                  parentCategorySlug={`${categoryParentSlug}/${category?.category?.slug as string}`}
                />
              )}

              <Products
                onTotalChange={setTotalProducts}
                categoryId={category?.category?.id}
                loadingCategory={loadingCategory}
              />
            </div>
          </div>
        </Wrapper>
      </PageGrid>
    </Container>
  )
}
